import { cardPaymentError, GENERAL_PAYMENT_ERROR } from './cardPaymentErrors';
import {
  CardApiResult,
  CardConfirmResult,
  CardFinaliseResult,
  CardOrderRequest,
  CardOrderResult,
  CardPaymentOutcome,
  CardPaymentStage,
  OpenCardOrder,
} from './types';

export const FINALISE_ATTEMPTS = 4;
export const FINALISE_INTERVAL_MS = 2000;

export type CardPaymentDeps = {
  createOrder: (request: CardOrderRequest) => Promise<CardApiResult<CardOrderResult>>;
  confirmPayment: (clientSecret: string) => Promise<CardConfirmResult>;
  finalise: (orderReference: string) => Promise<CardApiResult<CardFinaliseResult>>;
  wait: (ms: number) => Promise<void>;
  onStage: (stage: CardPaymentStage) => void;
};

export type CardPaymentRun = {
  outcome: CardPaymentOutcome;
  /** The order a retry may reuse, or null when the next attempt needs a new one. */
  openOrder: OpenCardOrder | null;
};

const sortKeys = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(sortKeys);
  }

  if (value && typeof value === 'object') {
    return Object.keys(value as object)
      .sort()
      .reduce((sorted: { [key: string]: unknown }, key) => {
        sorted[key] = sortKeys((value as { [key: string]: unknown })[key]);
        return sorted;
      }, {});
  }

  return value;
};

/** Identifies a request by its content, so two requests for the same order compare equal. */
export const requestKey = (request: CardOrderRequest): string => JSON.stringify(sortKeys(request));

export const canReuseOrder = (order: OpenCardOrder | null, key: string): order is OpenCardOrder =>
  !!order && order.bReusable && order.requestKey === key;

/** Stripe statuses after which the payment has been taken, or is being taken, whatever the call returned. */
const takenStatuses = ['succeeded', 'processing', 'requires_capture'];

/**
 * Takes one card payment: places the order (or reuses the open one), has Stripe take the payment, then
 * confirms it with the API. Once Stripe has the money the outcome is never 'failed', because showing an
 * error then invites a second payment.
 */
export const runCardPayment = async (
  request: CardOrderRequest,
  openOrder: OpenCardOrder | null,
  deps: CardPaymentDeps
): Promise<CardPaymentRun> => {
  const key = requestKey(request);
  let order: OpenCardOrder;

  if (canReuseOrder(openOrder, key)) {
    order = openOrder;
  } else {
    deps.onStage('creating');

    const created = await deps.createOrder(request);

    if (created.error) {
      return { outcome: { kind: 'failed', ...cardPaymentError(created.error.code) }, openOrder: null };
    }

    if (created.data.status === 'PAID') {
      return { outcome: { kind: 'paid' }, openOrder: null };
    }

    order = {
      requestKey: key,
      orderReference: created.data.orderReference,
      clientSecret: created.data.clientSecret,
      bReusable: true,
    };
  }

  deps.onStage('confirming');

  const confirmed = await deps.confirmPayment(order.clientSecret);

  if (confirmed.error) {
    const intentStatus = confirmed.error.payment_intent && confirmed.error.payment_intent.status;

    // Finalise is not called after a failed attempt: it would mark the order FAILED while a retry is still possible.
    if (!intentStatus || !takenStatuses.includes(intentStatus)) {
      return {
        outcome: { kind: 'failed', message: confirmed.error.message || GENERAL_PAYMENT_ERROR },
        openOrder: { ...order, bReusable: intentStatus !== 'canceled' },
      };
    }
  }

  deps.onStage('finalising');

  for (let attempt = 1; attempt <= FINALISE_ATTEMPTS; attempt++) {
    const finalised = await deps.finalise(order.orderReference);

    if (finalised.data && finalised.data.status === 'PAID') {
      return { outcome: { kind: 'paid' }, openOrder: null };
    }

    if (attempt < FINALISE_ATTEMPTS) {
      await deps.wait(FINALISE_INTERVAL_MS);
    }
  }

  return { outcome: { kind: 'received' }, openOrder: null };
};
